/**
 * OnPageLoadAction.tsx — Invisible component that executes a side-effect on
 * page load via `useEffect`.
 *
 * JSON trigger: `uiCompType.method: "renderOnPageLoadAction"`
 *
 * **Data field:** `sectionData.postLoadAction.method` — Name of the global
 * `window.*` function to call after mount (e.g. `"handleCustomerRecentlyViewed"`).
 * The function receives `sectionData.inputObj` as its argument.
 *
 * **Renders:** Nothing (returns `null`). Purely side-effect-driven.
 *
 * **Use cases:**
 *  - Triggering analytics events on page view
 *  - Loading personalised "recently viewed" product data
 *  - Any deferred window-level initialisation
 */
import React from "react";
import { SectionData, ComponentRenderProps } from "./index";

const OnPageLoadAction: React.FC<{ sectionData: SectionData; props: ComponentRenderProps }> = ({ sectionData, props }) => {
    React.useEffect(() => {
        const handler = () => {
            if (
                sectionData?.postLoadAction?.method &&
                typeof window !== "undefined" &&
                // @ts-ignore
                typeof (window as any)[sectionData.postLoadAction.method] === "function"
            ) {
                (window as any)[sectionData.postLoadAction.method](sectionData.inputObj, props);
            } else if (sectionData?.postLoadAction?.method) {
                console.warn(`OnPageLoadAction: Global function ${sectionData.postLoadAction.method} not found or not a function.`);
            }
        };
        handler();
    }, [sectionData, props]);
    return null;
};
export default OnPageLoadAction;
